import * as dagre from 'dagre';
import { GridState, GridNode, GridEdge } from '../types';

export interface LayoutOptions {
  rankdir?: 'TB' | 'BT' | 'LR' | 'RL';
  ranksep?: number;
  nodesep?: number;
  edgesep?: number;
  rankSep?: number;
  nodeSep?: number;
  edgeSep?: number;
}

export function applyAutomaticLayout(
  gridState: GridState, 
  options: LayoutOptions = {}
): GridState {
  const {
    rankdir = 'TB',
    ranksep = 100,
    nodesep = 100,
    edgesep = 20
  } = options;

  // Create a new directed graph
  const g = new dagre.graphlib.Graph({ directed: false, multigraph: false, compound: false });

  // Set graph layout options
  g.setGraph({
    rankdir,
    ranksep,
    nodesep,
    edgesep,
    marginx: 50,
    marginy: 50,
    acyclicer: 'greedy',
    ranker: 'network-simplex'
  });

  // Set default node and edge properties
  g.setDefaultNodeLabel(() => ({}));
  g.setDefaultEdgeLabel(() => ({}));

  // Add nodes to the graph
  gridState.nodes.forEach(node => {
    g.setNode(node.id, {
      width: 60,  // Node circle diameter + padding
      height: 90, // Node + labels height
      node: node
    });
  });

  // Add edges to the graph
  gridState.edges.forEach(edge => {
    g.setEdge(edge.from, edge.to, {
      edge: edge,
      weight: 1, // All edges have equal weight for layout purposes
      minlen: 1  // Minimum edge length
    });
  });

  // Apply the layout
  dagre.layout(g);

  // Extract positioned nodes
  const layoutedNodes: GridNode[] = gridState.nodes.map(node => {
    const dagreNode = g.node(node.id);
    return {
      ...node,
      x: dagreNode.x,
      y: dagreNode.y
    };
  });

  return {
    nodes: layoutedNodes,
    edges: gridState.edges
  };
}

export function applyHierarchicalLayout(gridState: GridState): GridState {
  // Identify generators, buses, and loads for hierarchical layout
  const generators = gridState.nodes.filter(n => n.type === 'generator');
  const buses = gridState.nodes.filter(n => n.type === 'bus');
  const loads = gridState.nodes.filter(n => n.type === 'load');

  // Use Dagre with specific settings for power grid hierarchical layout
  return applyAutomaticLayout(gridState, {
    rankdir: 'TB', // Top-down: generators at top, loads at bottom
    ranksep: 120,  // More space between ranks
    nodesep: 80,   // Less space between nodes in same rank
    edgesep: 10    // Minimal edge separation
  });
}

export function applyCircularLayout(gridState: GridState): GridState {
  const nodeCount = gridState.nodes.length;
  if (nodeCount === 0) return gridState;

  const centerX = 400;
  const centerY = 300;
  const radius = Math.max(150, nodeCount * 20); // Scale radius with node count

  const layoutedNodes: GridNode[] = gridState.nodes.map((node, index) => {
    const angle = (2 * Math.PI * index) / nodeCount;
    return {
      ...node,
      x: centerX + radius * Math.cos(angle),
      y: centerY + radius * Math.sin(angle)
    };
  });

  return {
    nodes: layoutedNodes,
    edges: gridState.edges
  };
}

export function applyForceLayout(gridState: GridState): GridState {
  // Simple force-directed layout using Dagre with LR direction
  // This tends to spread nodes more evenly and reduce edge crossings
  return applyAutomaticLayout(gridState, {
    rankdir: 'LR', // Left-right layout often has fewer crossings
    ranksep: 150,
    nodesep: 120,
    edgesep: 30
  });
}

// Utility function to detect and count edge crossings
export function countEdgeCrossings(gridState: GridState): number {
  const { nodes, edges } = gridState;
  let crossings = 0;

  // Create a map for fast node lookup
  const nodeMap = new Map(nodes.map(n => [n.id, n]));

  // Check all pairs of edges for intersections
  for (let i = 0; i < edges.length; i++) {
    for (let j = i + 1; j < edges.length; j++) {
      const edge1 = edges[i];
      const edge2 = edges[j];

      // Skip if edges share a node
      if (edge1.from === edge2.from || edge1.from === edge2.to ||
          edge1.to === edge2.from || edge1.to === edge2.to) {
        continue;
      }

      const node1a = nodeMap.get(edge1.from);
      const node1b = nodeMap.get(edge1.to);
      const node2a = nodeMap.get(edge2.from);
      const node2b = nodeMap.get(edge2.to);

      if (!node1a || !node1b || !node2a || !node2b) continue;

      // Check if line segments intersect
      if (doLinesIntersect(
        node1a.x, node1a.y, node1b.x, node1b.y,
        node2a.x, node2a.y, node2b.x, node2b.y
      )) {
        crossings++;
      }
    }
  }

  return crossings;
}

// Helper function to check if two line segments intersect
function doLinesIntersect(
  x1: number, y1: number, x2: number, y2: number,
  x3: number, y3: number, x4: number, y4: number
): boolean {
  const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  
  if (Math.abs(denominator) < 1e-10) {
    return false; // Lines are parallel
  }

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denominator;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denominator;

  return t >= 0 && t <= 1 && u >= 0 && u <= 1;
}

// Function to find the best layout with minimum edge crossings
export function findBestLayout(gridState: GridState): { layout: GridState; crossings: number; layoutType: string } {
  const layouts = [
    { name: 'Hierarchical', layout: applyHierarchicalLayout(gridState) },
    { name: 'Force-Directed', layout: applyForceLayout(gridState) },
    { name: 'Top-Down', layout: applyAutomaticLayout(gridState, { rankdir: 'TB' }) },
    { name: 'Left-Right', layout: applyAutomaticLayout(gridState, { rankdir: 'LR' }) },
    { name: 'Circular', layout: applyCircularLayout(gridState) }
  ];

  let bestLayout = layouts[0];
  let minCrossings = countEdgeCrossings(bestLayout.layout);

  for (const layoutOption of layouts) {
    const crossings = countEdgeCrossings(layoutOption.layout);
    if (crossings < minCrossings) {
      minCrossings = crossings;
      bestLayout = layoutOption;
    }
  }

  return {
    layout: bestLayout.layout,
    crossings: minCrossings,
    layoutType: bestLayout.name
  };
}